import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Outlet, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  LayoutDashboard, 
  Package, 
  BarChart3, 
  LogOut,
  Menu,
  X,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Truck,
  Mail,
  FlaskConical
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { getCurrentUser, getAppSettings } from '@/lib/api';

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [userRole, setUserRole] = useState(localStorage.getItem('admin_role') || 'admin');
  const [testMode, setTestMode] = useState(false);

  // Poll test mode status
  useEffect(() => {
    const checkTestMode = async () => {
      try {
        const settings = await getAppSettings();
        setTestMode(!!settings.test_mode);
      } catch (e) { console.warn('TestMode check failed:', e.message); }
    };
    checkTestMode();
    const interval = setInterval(checkTestMode, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (!mobile) setIsSidebarOpen(true);
      else setIsSidebarOpen(false);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Check for auto-login token from Shopify
    const urlToken = searchParams.get('token');
    const isAuto = searchParams.get('auto');
    
    if (urlToken && isAuto) {
      localStorage.setItem('admin_token', urlToken);
      searchParams.delete('token');
      searchParams.delete('auto');
      setSearchParams(searchParams);
      return;
    }
    
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admindea26');
      return;
    }

    // Fetch user role
    const fetchRole = async () => {
      try {
        const user = await getCurrentUser();
        setUserRole(user.role);
        localStorage.setItem('admin_role', user.role);
      } catch (e) {
        // Token invalid
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_role');
        navigate('/admindea26');
      }
    };
    fetchRole();
  }, [navigate, searchParams, setSearchParams]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_role');
    localStorage.removeItem('admin_email');
    navigate('/admindea26');
  };

  const allMenuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: LayoutDashboard,
      path: '/admindea26/dashboard',
      roles: ['admin']
    },
    {
      id: 'resi',
      label: 'Resi',
      icon: Package,
      path: '/admindea26/resi',
      roles: ['admin', 'logistics']
    },
    {
      id: 'eccezioni',
      label: 'Eccezioni',
      icon: ShieldCheck,
      path: '/admindea26/eccezioni',
      roles: ['admin']
    },
    {
      id: 'analisi',
      label: 'Analisi',
      icon: BarChart3,
      path: '/admindea26/analisi',
      roles: ['admin']
    },
    {
      id: 'pannello',
      label: 'Pannello di Controllo',
      icon: SlidersHorizontal,
      path: '/admindea26/pannello-controllo',
      roles: ['admin']
    },
    {
      id: 'email-templates',
      label: 'Template Email',
      icon: Mail,
      path: '/admindea26/email-templates',
      roles: ['admin']
    },
    {
      id: 'simulatore',
      label: 'Simulatore Test',
      icon: FlaskConical,
      path: '/admindea26/simulatore',
      roles: ['admin']
    },
    {
      id: 'impostazioni',
      label: 'Impostazioni',
      icon: Settings,
      path: '/admindea26/impostazioni',
      roles: ['admin']
    }
  ];

  const menuItems = allMenuItems.filter(item => item.roles.includes(userRole));

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile overlay */}
      {isMobile && isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/40 z-40"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
      
      {/* Mobile top bar */}
      {isMobile && (
        <div className="fixed top-0 left-0 right-0 z-30 bg-white border-b border-gray-200 h-14 flex items-center px-4">
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="p-2 hover:bg-gray-100 rounded-lg"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5 text-gray-700" />
          </button>
          <span className="ml-3 font-heading text-sm tracking-tight">
            {userRole === 'logistics' ? 'Logistica' : 'Admin Panel'}
          </span>
        </div>
      )}
      
      {/* Sidebar */}
      <aside 
        className={cn(
          "fixed inset-y-0 left-0 z-50 bg-white border-r border-gray-200 transition-all duration-300 flex flex-col",
          isMobile
            ? cn("w-64", isSidebarOpen ? "translate-x-0" : "-translate-x-full")
            : cn(isSidebarOpen ? "w-64" : "w-20")
        )}
      >
        {/* Logo/Header */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200">
          {isSidebarOpen && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex items-center gap-2"
            >
              <div className="w-8 h-8 bg-primary flex items-center justify-center">
                {userRole === 'logistics' ? (
                  <Truck className="w-5 h-5 text-white" />
                ) : (
                  <Package className="w-5 h-5 text-white" />
                )}
              </div>
              <span className="font-heading text-lg tracking-tight">
                {userRole === 'logistics' ? 'Logistica' : 'Admin Panel'}
              </span>
            </motion.div>
          )}
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            {isSidebarOpen ? (
              <X className="w-5 h-5 text-gray-500" />
            ) : (
              <Menu className="w-5 h-5 text-gray-500" />
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-6 px-3">
          <ul className="space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              
              return (
                <li key={item.id}>
                  <button
                    onClick={() => {
                      navigate(item.path);
                      if (isMobile) setIsSidebarOpen(false);
                    }}
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all",
                      active 
                        ? "bg-primary text-white shadow-md" 
                        : "text-gray-600 hover:bg-gray-100"
                    )}
                    data-testid={`nav-${item.id}`}
                  >
                    <Icon className={cn("w-5 h-5 flex-shrink-0", active ? "text-white" : "text-gray-500")} />
                    {isSidebarOpen && (
                      <motion.span 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="font-medium text-sm"
                      >
                        {item.label}
                      </motion.span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Logout */}
        <div className="p-3 border-t border-gray-200">
          <button
            onClick={handleLogout}
            className={cn(
              "w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all",
              "text-red-600 hover:bg-red-50"
            )}
            data-testid="nav-logout"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {isSidebarOpen && (
              <span className="font-medium text-sm">Esci</span>
            )}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main 
        className={cn(
          "flex-1 transition-all duration-300 min-w-0",
          isMobile 
            ? "ml-0 pt-14" 
            : isSidebarOpen ? "ml-64" : "ml-20"
        )}
      >
        {testMode && (
          <div className="bg-amber-500 text-white text-center py-2 px-4 text-sm font-semibold flex items-center justify-center gap-2" data-testid="test-mode-banner">
            <FlaskConical className="w-4 h-4" />
            TEST MODE ATTIVA — Le email non vengono inviate ai clienti
          </div>
        )}
        <Outlet context={{ userRole, testMode, isSidebarOpen, isMobile }} />
      </main>
    </div>
  );
};

export default AdminLayout;
